import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MoreVertical } from "lucide-react";
import useAnchoredPanel, { anchoredPanelStyle } from "./useAnchoredPanel";

export interface RowAction {
  label: string;
  onSelect: () => void;
  /** Renders the item in red, for the destructive entry in the list. */
  destructive?: boolean;
  /**
   * Greys the entry out while leaving it selectable, so `onSelect` can say why
   * it is off — the same arrangement `Button`'s `onDisabledClick` makes, and
   * for the same reason: an entry that cannot be reached cannot explain itself.
   */
  disabled?: boolean;
}

export interface RowActionsMenuProps {
  actions: RowAction[];
  /** Names the trigger, e.g. "Actions for Weight loss 6 months". */
  label: string;
}

const MENU_WIDTH = 160;
const MAX_HEIGHT = 240;

/**
 * The three-dot menu at the end of a table row. Only one is open at a time
 * because each instance closes on any outside pointer press — including the
 * press that opens another row's menu.
 */
const RowActionsMenu: React.FC<RowActionsMenuProps> = ({ actions, label }) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const { rect, openUpward } = useAnchoredPanel(triggerRef, open, MAX_HEIGHT);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !triggerRef.current?.contains(target) &&
        !panelRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return (
    // Stops the press from reaching a clickable row underneath.
    <div
      ref={triggerRef}
      className="relative inline-flex"
      onClick={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className="p-1 rounded-md text-ink-muted hover:bg-[#F4F7F8] hover:text-ink transition-colors"
      >
        <MoreVertical className="w-4 h-4" />
      </button>

      {open &&
        rect &&
        createPortal(
          <div
            ref={panelRef}
            role="menu"
            aria-label={label}
            // Right-aligned to the trigger: the menu is wider than the kebab,
            // so hanging it off the left edge would push it past the table.
            style={{
              ...anchoredPanelStyle(rect, openUpward, MAX_HEIGHT, MENU_WIDTH),
              left: rect.right - MENU_WIDTH,
            }}
            className="z-[60] overflow-y-auto rounded-lg border border-line bg-white py-1 shadow-[0_8px_24px_rgba(11,27,31,0.12)]"
          >
            {actions.map((action) => (
              <button
                key={action.label}
                type="button"
                role="menuitem"
                aria-disabled={action.disabled || undefined}
                onClick={() => {
                  setOpen(false);
                  action.onSelect();
                }}
                className={`w-full px-4 py-2 text-left text-[14px] transition-colors ${
                  action.disabled
                    ? "cursor-not-allowed text-ink-subtle"
                    : action.destructive
                    ? "text-[#D14343] hover:bg-[#FDECEC]"
                    : "text-ink hover:bg-[#F4F7F8]"
                }`}
              >
                {action.label}
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
};

export default RowActionsMenu;
